import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import TopBar from '../Dashboard/TopBar';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { Eye, Download, Trash2, Plus, X, Table as TableIcon, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const QRGenrator = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewQR, setViewQR] = useState(null);

  const [tableNumber, setTableNumber] = useState('');
  const [status, setStatus] = useState('active');

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7;
  const brandColor = "#ae7d54";

  const qrIconSettings = {
    src: "/plogo.png",
    height: 60,
    width: 60,
    excavate: true,
  };

  const fetchTables = async () => {
    try {
      const res = await axios.post('https://admin.theporch.in/api/tables/fetch');
      const sortedData = res.data.sort((a, b) => parseInt(a.table_number) - parseInt(b.table_number));
      setTables(sortedData);
    } catch (err) { 
      console.error("Fetch error:", err);
    }
  };

  useEffect(() => { fetchTables(); }, []);

  const handleSave = async () => {
    if (!tableNumber) return alert("Enter Table Number");
    const exists = tables.some(t => t.table_number.toString() === tableNumber.toString());
    if (exists) return alert(`Table ${tableNumber} already exists!`);

    const domain = "https://theporch.in/table";
    const qrLink = `${domain}?table=${tableNumber}`;
    const tableCode = `PRC-${tableNumber}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

    try {
      await axios.post('https://admin.theporch.in/api/tables/add', {
        table_number: tableNumber,
        status: status,
        table_code: tableCode,
        qr_link: qrLink
      });
      setShowAddModal(false);
      setTableNumber('');
      fetchTables();
    } catch (err) { alert("Error saving table."); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this table?")) {
      try {
        await axios.post('https://admin.theporch.in/api/tables/delete', { id: id });
        fetchTables();
      } catch (err) {
        console.error("Delete Error details:", err.response);
        alert(`Delete Failed (Status: ${err.response?.status}). Check if the backend route app.post('/api/tables/delete') is correctly defined.`);
      }
    }
  };

  const downloadQR = (id, tNum) => {
    const canvas = document.getElementById(id);
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `ThePorch_Table_${tNum}.png`;
    link.click();
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tables.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(tables.length / rowsPerPage);

  return (
    <div className="layout-wrapper" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc', fontFamily: "'Inter', sans-serif", overflowX: 'hidden' }}>
      <style>{`
        .main-container { flex: 1; margin-left: 260px; width: calc(100% - 260px); transition: 0.3s ease; display: flex; flex-direction: column; min-width: 0; }
        .content-wrapper { padding: 40px; width: 100%; max-width: 1800px; margin: 0 auto; box-sizing: border-box; }
        
        .header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; flex-wrap: wrap; gap: 15px; }
        .header-title h2 { margin: 0; font-size: 1.5rem; }
        
        .add-btn { background: ${brandColor}; color: white; border: none; padding: 12px 24px; border-radius: 12px; cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: 8px; transition: 0.2s; box-shadow: 0 4px 12px rgba(174, 125, 84, 0.2); }
        .add-btn:hover { filter: brightness(1.1); transform: translateY(-1px); }

        .table-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); width: 100%; }
        .custom-table { width: 100%; border-collapse: collapse; }
        .custom-table th { background: #f8fafc; padding: 18px; font-size: 12px; font-weight: 700; text-transform: uppercase; color: #64748b; border-bottom: 1px solid #e2e8f0; text-align: center; }
        .custom-table td { padding: 16px; border-bottom: 1px solid #f1f5f9; color: #334155; text-align: center; vertical-align: middle; }

        .icon-btn { width: 38px; height: 38px; border-radius: 10px; border: none; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; margin: 0 4px; }
        .btn-view { background: #fdf8f3; color: ${brandColor}; }
        .btn-download { background: #f0fdf4; color: #16a34a; }
        .btn-delete { background: #fef2f2; color: #dc2626; }

        .pagination { display: flex; justify-content: space-between; align-items: center; padding: 15px 25px; background: white; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px; }
        .page-node { padding: 8px 16px; border-radius: 8px; border: 1px solid #e2e8f0; background: white; color: #475569; cursor: pointer; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 5px; }
        .page-node:disabled { opacity: 0.4; cursor: not-allowed; }

        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(4px); padding: 20px; }
        .modal-content { background: white; padding: 30px; border-radius: 24px; width: 100%; max-width: 400px; position: relative; border: 1px solid #e2e8f0; box-sizing: border-box; }

        /* RESPONSIVE BREAKPOINTS */
        @media (max-width: 1024px) { 
          .main-container { margin-left: 0; width: 100%; } 
          .content-wrapper { padding: 20px; } 
        }

        @media (max-width: 768px) {
          .header-section { flex-direction: column; align-items: flex-start; }
          .add-btn { width: 100%; justify-content: center; }
          
          /* Force table to not be a table */
          .custom-table, .custom-table thead, .custom-table tbody, .custom-table th, .custom-table td, .custom-table tr { 
            display: block; 
          }
          
          /* Hide table headers (but keep for accessibility) */
          .custom-table thead tr { 
            position: absolute;
            top: -9999px;
            left: -9999px;
          }
          
          .custom-table tr { 
            border: 1px solid #e2e8f0;
            margin-bottom: 15px;
            border-radius: 15px;
            overflow: hidden;
            background: #fff;
          }
          
          .custom-table td { 
            border: none;
            border-bottom: 1px solid #f1f5f9; 
            position: relative;
            padding-left: 45%; 
            text-align: right;
            min-height: 45px;
            display: flex;
            align-items: center;
            justify-content: flex-end;
          }
          
          .custom-table td:last-child { border-bottom: 0; }
          
          /* Label the data */
          .custom-table td:before { 
            content: attr(data-label);
            position: absolute;
            left: 15px;
            width: 40%; 
            padding-right: 10px; 
            white-space: nowrap;
            text-align: left;
            font-weight: 700;
            color: #64748b;
            font-size: 11px;
            text-transform: uppercase;
          }

          .icon-btn { width: 42px; height: 42px; margin: 0 2px; }
          .pagination { justify-content: center; }
          .pagination span { width: 100%; text-align: center; margin-bottom: 10px; }
        }
      `}</style>

      <Sidebar isOpen={isSidebarOpen} />
      
      <main className="main-container">
        <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />
        
        <div className="content-wrapper">
          <div className="header-section">
            <div className="header-title">
              <h2 style={{ color: '#1e293b' }}>QR Table Management</h2>
            </div>
            <button className="add-btn" onClick={() => setShowAddModal(true)}>
              <Plus size={18} /> Add QR Table
            </button>
          </div>

          <div className="table-card">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Table Number</th>
                  <th>Status</th>
                  <th>Unique Code</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map(t => (
                  <tr key={t.id}>
                    <td data-label="Table Number" style={{ fontWeight: '700' }}>Table {t.table_number}</td>
                    <td data-label="Status">
                      <span style={{ padding: '6px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', background: t.status === 'active' ? '#dcfce7' : '#fee2e2', color: t.status === 'active' ? '#15803d' : '#b91c1c' }}>
                        {t.status.toUpperCase()}
                      </span>
                    </td>
                    <td data-label="Unique Code">
                      <span style={{ background: '#f8fafc', padding: '6px 10px', borderRadius: '8px', fontFamily: 'monospace', border: '1px solid #e2e8f0', color: brandColor, fontSize: '12px' }}>
                        {t.table_code}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button className="icon-btn btn-view" title="View" onClick={() => setViewQR(t)}><Eye size={18} /></button>
                        <button className="icon-btn btn-download" title="Download" onClick={() => downloadQR(`qr-${t.id}`, t.table_number)}><Download size={18} /></button>
                        <button className="icon-btn btn-delete" title="Delete" onClick={() => handleDelete(t.id)}><Trash2 size={18} /></button>
                      </div>
                      <div style={{ display: 'none' }}>
                        <QRCodeCanvas id={`qr-${t.id}`} value={t.qr_link} size={1024} level="H" includeMargin={true} imageSettings={{ ...qrIconSettings, height: 220, width: 220 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Page {currentPage} of {totalPages || 1}</span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="page-node" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={16} /> Prev</button>
                <button className="page-node" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next <ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: '20px', color: brandColor, marginTop: 0 }}>Register Table</h3>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>TABLE NUMBER</label>
                <input style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '5px', boxSizing: 'border-box' }} type="number" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="e.g. 5" />
            </div>
            <div style={{ marginBottom: '25px' }}>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#64748b' }}>STATUS</label>
                <select style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '5px', boxSizing: 'border-box', background: 'white' }} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
            </div>
            <button className="add-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={handleSave}>Create QR Code</button>
            <button onClick={() => setShowAddModal(false)} style={{ width: '100%', marginTop: '10px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {viewQR && (
        <div className="modal-overlay" onClick={() => setViewQR(null)}>
          <div className="modal-content" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 5px 0' }}>Table {viewQR.table_number}</h3>
            <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '20px' }}>The Porch Branding Enabled</p>
            <div style={{ background: 'white', padding: '15px', border: '1px solid #f1f5f9', borderRadius: '20px', display: 'inline-block', maxWidth: '100%', boxSizing: 'border-box' }}>
              <QRCodeCanvas value={viewQR.qr_link} size={200} level="H" includeMargin={true} imageSettings={qrIconSettings} style={{ maxWidth: '100%', height: 'auto' }} />
            </div>
            <button className="add-btn" style={{ width: '100%', marginTop: '20px', justifyContent: 'center' }} onClick={() => setViewQR(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRGenrator;